import { Link } from "react-router-dom";
import { Linkedin, Twitter, Instagram, Youtube, Mail, Phone, MapPin, Globe } from "lucide-react";
import { SITE_URL, EMAILS, PHONE_PLACEHOLDER, ADDRESS, TAGLINE } from "@/constants/site";

const Footer = () => (
    <footer data-testid="site-footer" className="grain relative bg-obsidian text-[#F5F5F4]">
        <div className="mx-auto max-w-7xl px-6 py-16 sm:px-10 lg:py-20">
            <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-5">
                <div className="lg:col-span-2">
                    <div className="flex min-w-[250px] items-center gap-3" data-testid="footer-logo">
                        <img
                            src="/assets/logo-emblem-light.png"
                            alt="Coaching Crafters AI emblem"
                            width={56}
                            height={56}
                            className="h-14 w-14 object-contain"
                        />
                        <span className="whitespace-nowrap font-serif text-2xl font-semibold tracking-tight text-[#F5F5F4]">
                            Coaching Crafters <span className="text-ember-soft">AI</span>
                        </span>
                    </div>
                    <p className="mt-2 font-serif text-sm italic text-[#F5F5F4]/45" data-testid="footer-tagline">
                        {TAGLINE}
                    </p>
                    <p className="mt-4 max-w-sm text-sm leading-relaxed text-[#F5F5F4]/60">
                        A technology and solutions company crafting AI web software, ERP platforms,
                        interactive panels and solar systems — so schools, businesses and institutions
                        can work smarter, teach better and grow sustainably.
                    </p>
                    <div className="mt-6 flex gap-3">
                        {[
                            { icon: Linkedin, id: "linkedin", label: "LinkedIn" },
                            { icon: Twitter, id: "twitter", label: "Twitter" },
                            { icon: Instagram, id: "instagram", label: "Instagram" },
                            { icon: Youtube, id: "youtube", label: "YouTube" },
                        ].map(({ icon: Icon, id, label }) => (
                            <a
                                key={id}
                                href={`#${id}`}
                                aria-label={label}
                                data-testid={`footer-social-${id}`}
                                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-[#F5F5F4]/70 transition-colors duration-200 hover:border-ember hover:text-ember"
                            >
                                <Icon className="h-4 w-4" />
                            </a>
                        ))}
                    </div>
                </div>

                <div>
                    <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-ember-soft">Explore</h3>
                    <ul className="mt-5 space-y-3 text-sm">
                        {[
                            { label: "Home", to: "/", id: "home" },
                            { label: "About Us", to: "/about", id: "about" },
                            { label: "Our Products", to: "/products", id: "products" },
                            { label: "Contact Us", to: "/contact", id: "contact" },
                            { label: "Our Policy", to: "/policy", id: "policy" },
                        ].map((l) => (
                            <li key={l.id}>
                                <Link
                                    to={l.to}
                                    data-testid={`footer-explore-${l.id}-link`}
                                    className="text-[#F5F5F4]/70 transition-colors hover:text-ember"
                                >
                                    {l.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                <div>
                    <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-ember-soft">Company</h3>
                    <ul className="mt-5 space-y-3 text-sm">
                        {[
                            { label: "Privacy Policy", to: "/privacy-policy", id: "privacy" },
                            { label: "Login", to: "/login", id: "login" },
                            { label: "Book a Demo", to: "/contact?demo=1", id: "demo" },
                        ].map((l) => (
                            <li key={l.id}>
                                <Link
                                    to={l.to}
                                    data-testid={`footer-company-${l.id}-link`}
                                    className="text-[#F5F5F4]/70 transition-colors hover:text-ember"
                                >
                                    {l.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                <div>
                    <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-ember-soft">Contact</h3>
                    <ul className="mt-5 space-y-4 text-sm text-[#F5F5F4]/70">
                        <li className="flex items-start gap-3">
                            <Globe className="mt-0.5 h-4 w-4 shrink-0 text-ember" />
                            <a
                                href={SITE_URL}
                                data-testid="footer-website-link"
                                className="transition-colors hover:text-ember"
                            >
                                coachingcraftersai.com
                            </a>
                        </li>
                        <li className="flex items-start gap-3">
                            <Mail className="mt-0.5 h-4 w-4 shrink-0 text-ember" />
                            <span>
                                <span className="block text-xs uppercase tracking-wider text-[#F5F5F4]/40">Sales</span>
                                <a href={`mailto:${EMAILS.sales}`} data-testid="footer-sales-email-link" className="transition-colors hover:text-ember">
                                    {EMAILS.sales}
                                </a>
                            </span>
                        </li>
                        <li className="flex items-start gap-3">
                            <Mail className="mt-0.5 h-4 w-4 shrink-0 text-ember" />
                            <span>
                                <span className="block text-xs uppercase tracking-wider text-[#F5F5F4]/40">Support</span>
                                <a href={`mailto:${EMAILS.support}`} data-testid="footer-support-email-link" className="transition-colors hover:text-ember">
                                    {EMAILS.support}
                                </a>
                            </span>
                        </li>
                        <li className="flex items-start gap-3">
                            <Phone className="mt-0.5 h-4 w-4 shrink-0 text-ember" />
                            <span data-testid="footer-phone">{PHONE_PLACEHOLDER}</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-ember" />
                            <span data-testid="footer-address">{ADDRESS}</span>
                        </li>
                    </ul>
                </div>
            </div>

            <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 text-xs text-[#F5F5F4]/50 sm:flex-row">
                <p data-testid="footer-copyright">
                    <span className="block">© 2026 Sarvacharya Education Pvt Ltd. All rights reserved.</span>
                    <span className="mt-1 block">Coaching Crafters AI is a brand of Sarvacharya Education Pvt Ltd.</span>
                </p>
                <p className="font-serif italic">Crafted with intent — achieving all goals.</p>
            </div>
        </div>
    </footer>
);

export default Footer;
